import { useState, useEffect, useRef } from "react";
import { useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { VoiceToTextTextarea as Textarea } from "@/components/voice-field";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Plus, Pencil, Trash2, Link as LinkIcon, Loader2, ChevronLeft, ExternalLink, Clock, Wand2, Calendar, CheckCircle, AlertCircle, Info, Mail } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { Talent, BookingForm, Region, ClientTypeRecord } from "@shared/schema";

interface CustomTimeslot {
  day: string;
  startTime: string;
  endTime: string;
  label?: string;
}

const DAYS_OF_WEEK = [
  { value: "monday", label: "Monday" },
  { value: "tuesday", label: "Tuesday" },
  { value: "wednesday", label: "Wednesday" },
  { value: "thursday", label: "Thursday" },
  { value: "friday", label: "Friday" },
  { value: "saturday", label: "Saturday" },
  { value: "sunday", label: "Sunday" },
];

interface GoogleCalendar {
  id: string;
  summary: string;
  primary?: boolean;
  accessRole: string;
}

interface BookingFormsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  talent: Talent | null;
  defaultTab?: string;
}

export function BookingFormsDialog({ open, onOpenChange, talent, defaultTab }: BookingFormsDialogProps) {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [view, setView] = useState<"list" | "edit" | "create">("list");
  const [selectedForm, setSelectedForm] = useState<BookingForm | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    clientTypes: [] as string[],
    regionIds: [] as string[],
    calendarIds: [] as string[],
    openAvailability: false,
    availabilityMode: "calendar" as "calendar" | "custom",
    customTimeslots: [] as CustomTimeslot[],
    formTitle: "",
    formSubtitle: "",
    formDisclaimer: "",
    isActive: true,
  });

  const { data: bookingForms = [], isLoading, refetch } = useQuery<BookingForm[]>({
    queryKey: ["/api/talent", talent?.id, "booking-forms"],
    enabled: !!talent?.id && open,
    staleTime: 0,
  });

  // Force refetch when dialog opens to ensure fresh data
  useEffect(() => {
    if (open && talent?.id) {
      refetch();
    }
  }, [open, talent?.id, refetch]);

  const { data: regions = [] } = useQuery<Region[]>({
    queryKey: ["/api/regions"],
    enabled: open,
  });

  const { data: clientTypes = [] } = useQuery<ClientTypeRecord[]>({
    queryKey: ["/api/client-types"],
    enabled: open,
  });

  // Talent-specific calendar endpoint (follows talent → user OAuth chain)
  const [activeTab, setActiveTab] = useState<string>(defaultTab || "forms");

  // Sync activeTab when defaultTab changes (e.g. opening from Calendars button)
  useEffect(() => {
    if (open) setActiveTab(defaultTab || "forms");
  }, [open, defaultTab]);

  const { data: calendarsData, isLoading: calendarsLoading, error: calendarsError } = useQuery<{
    calendars: GoogleCalendar[];
    connected?: boolean;
    source?: string;
    connectedEmail?: string;
  }>({
    queryKey: ["/api/talent", talent?.id, "calendars"],
    queryFn: async () => {
      const res = await fetch(`/api/talent/${talent!.id}/calendars`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load calendars");
      return res.json();
    },
    enabled: open && !!talent?.id,
  });

  const availableCalendars = calendarsData?.calendars || [];
  const hasTalentCalendar = calendarsData?.connected ?? false;
  const calendarSource = calendarsData?.source;
  const connectedEmail = calendarsData?.connectedEmail || '';

  // State for the inline Calendars tab within the list view
  const [selectedCalendarIds, setSelectedCalendarIds] = useState<string[]>([]);

  // Sync selectedCalendarIds from talent whenever talent changes
  useEffect(() => {
    setSelectedCalendarIds(talent?.calendarIds ?? []);
  }, [talent?.id]);

  const updateCalendarsMutation = useMutation({
    mutationFn: async (calendarIds: string[]) => {
      if (!talent) throw new Error("No talent");
      const res = await apiRequest("PATCH", `/api/talent/${talent.id}`, {
        calendarIds: calendarIds.length > 0 ? calendarIds : null,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/talent"] });
      toast({ title: "Calendars saved", description: `Calendar settings for ${talent?.name} updated.` });
    },
    onError: (e: any) => {
      toast({ title: "Error", description: e.message || "Failed to save calendars.", variant: "destructive" });
    },
  });

  const sendCalInviteMutation = useMutation({
    mutationFn: async () => {
      if (!talent) throw new Error("No talent");
      const res = await apiRequest("POST", `/api/talent/${talent.id}/calendar/invite`);
      return res.json();
    },
    onSuccess: (data: any) => {
      toast({ title: "Invite sent", description: data.message || `Calendar invite sent to ${talent?.email}` });
    },
    onError: (e: any) => {
      toast({ title: "Error", description: e.message || "Failed to send invite.", variant: "destructive" });
    },
  });
  const activeClientTypes = clientTypes.filter(ct => ct.isActive);

  const advancedAfterSaveRef = useRef(false);

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      return apiRequest("POST", "/api/booking-forms", {
        ...data,
        customTimeslotsJson: data.availabilityMode === "custom" ? JSON.stringify(data.customTimeslots) : null,
        talentId: talent?.id,
      });
    },
    onSuccess: (created: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/talent", talent?.id, "booking-forms"] });
      const newId = created?.id;
      const goAdvanced = advancedAfterSaveRef.current;
      advancedAfterSaveRef.current = false;
      if (goAdvanced && newId) {
        resetForm();
        onOpenChange(false);
        navigate(`/admin/form-builder/${newId}`);
        return;
      }
      toast({ title: "Booking Form Created", description: "The new booking form has been created." });
      resetForm();
      setView("list");
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to create booking form", variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: typeof formData }) => {
      return apiRequest("PATCH", `/api/booking-forms/${id}`, {
        ...data,
        customTimeslotsJson: data.availabilityMode === "custom" ? JSON.stringify(data.customTimeslots) : null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/talent", talent?.id, "booking-forms"] });
      toast({ title: "Booking Form Updated", description: "The booking form has been updated." });
      setView("list");
      resetForm();
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to update booking form", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest("DELETE", `/api/booking-forms/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/talent", talent?.id, "booking-forms"] });
      toast({ title: "Booking Form Deleted", description: "The booking form has been removed." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to delete booking form", variant: "destructive" });
    },
  });

  const resetForm = () => {
    setFormData({
      name: "",
      slug: "",
      clientTypes: [],
      regionIds: [],
      calendarIds: [],
      openAvailability: false,
      availabilityMode: "calendar",
      customTimeslots: [],
      formTitle: "",
      formSubtitle: "",
      formDisclaimer: "",
      isActive: true,
    });
    setSelectedForm(null);
  };

  const parseCustomTimeslots = (json: string | null): CustomTimeslot[] => {
    if (!json) return [];
    try {
      return JSON.parse(json);
    } catch {
      return [];
    }
  };

  const handleEditForm = (form: BookingForm) => {
    setSelectedForm(form);
    setFormData({
      name: form.name ?? "",
      slug: form.slug ?? "",
      clientTypes: form.clientTypes ?? [],
      regionIds: (form as any).regionIds ?? [],
      calendarIds: (form as any).calendarIds ?? [],
      openAvailability: !!(form as any).openAvailability,
      availabilityMode: ((form as any).availabilityMode === "custom" ? "custom" : "calendar") as "calendar" | "custom",
      customTimeslots: parseCustomTimeslots((form as any).customTimeslotsJson ?? null),
      formTitle: (form as any).formTitle ?? "",
      formSubtitle: (form as any).formSubtitle ?? "",
      formDisclaimer: (form as any).formDisclaimer ?? "",
      isActive: form.isActive ?? true,
    });
    setView("edit");
  };

  const handleOpenAdvanced = () => {
    if (view === "edit" && selectedForm) {
      onOpenChange(false);
      navigate(`/admin/form-builder/${selectedForm.id}`);
      return;
    }
    if (view === "create") {
      if (!formData.name.trim() || !formData.slug.trim()) {
        toast({ title: "Validation Error", description: "Name and URL slug are required.", variant: "destructive" });
        return;
      }
      advancedAfterSaveRef.current = true;
      createMutation.mutate(formData);
    }
  };

  const handleCreateNew = () => {
    resetForm();
    setView("create");
  };

  const handleSave = () => {
    if (!formData.name.trim() || !formData.slug.trim()) {
      toast({ title: "Validation Error", description: "Name and URL slug are required.", variant: "destructive" });
      return;
    }

    if (view === "create") {
      createMutation.mutate(formData);
    } else if (selectedForm) {
      updateMutation.mutate({ id: selectedForm.id, data: formData });
    }
  };

  const handleDelete = (form: BookingForm) => {
    if (confirm(`Are you sure you want to delete "${form.name}"?`)) {
      deleteMutation.mutate(form.id);
    }
  };

  const toggleClientType = (typeName: string) => {
    setFormData(prev => ({
      ...prev,
      clientTypes: prev.clientTypes.includes(typeName)
        ? prev.clientTypes.filter(t => t !== typeName)
        : [...prev.clientTypes, typeName],
    }));
  };

  const toggleRegion = (regionId: string) => {
    setFormData(prev => ({
      ...prev,
      regionIds: prev.regionIds.includes(regionId)
        ? prev.regionIds.filter(r => r !== regionId)
        : [...prev.regionIds, regionId],
    }));
  };

  const toggleCalendar = (calendarId: string) => {
    setFormData(prev => ({
      ...prev,
      calendarIds: prev.calendarIds.includes(calendarId)
        ? prev.calendarIds.filter(c => c !== calendarId)
        : [...prev.calendarIds, calendarId],
    }));
  };

  const addTimeslot = () => {
    setFormData(prev => ({
      ...prev,
      customTimeslots: [
        ...prev.customTimeslots,
        { day: "monday", startTime: "09:00", endTime: "17:00", label: "" },
      ],
    }));
  };

  const updateTimeslot = (index: number, field: keyof CustomTimeslot, value: string) => {
    setFormData(prev => ({
      ...prev,
      customTimeslots: prev.customTimeslots.map((slot, i) =>
        i === index ? { ...slot, [field]: value } : slot
      ),
    }));
  };

  const removeTimeslot = (index: number) => {
    setFormData(prev => ({
      ...prev,
      customTimeslots: prev.customTimeslots.filter((_, i) => i !== index),
    }));
  };

  const generateSlug = (name: string) => {
    return name.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  useEffect(() => {
    if (view === "create" && formData.name && !formData.slug) {
      setFormData(prev => ({ ...prev, slug: generateSlug(prev.name) }));
    }
  }, [formData.name, view]);

  useEffect(() => {
    if (!open) {
      setView("list");
      resetForm();
    }
  }, [open]);

  const getBookingUrl = (form: BookingForm) => {
    const talentSlug = talent?.bookingSlug || talent?.id;
    return `/book/${talentSlug}/${form.slug}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {view !== "list" && (
              <Button variant="ghost" size="icon" onClick={() => setView("list")} data-testid="button-back-to-list">
                <ChevronLeft className="w-4 h-4" />
              </Button>
            )}
            {view === "list" ? "Booking Forms" : view === "create" ? "Create Booking Form" : "Edit Booking Form"}
          </DialogTitle>
          <DialogDescription>
            {view === "list" 
              ? `Manage booking forms for ${talent?.name}. Each form can target specific client types.`
              : view === "create"
              ? "Create a new booking form with specific client type targeting."
              : `Editing ${selectedForm?.name}`}
          </DialogDescription>
        </DialogHeader>

        {view === "list" ? (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="w-full mb-4">
              <TabsTrigger value="forms" className="flex-1" data-testid="tab-forms">
                Forms
              </TabsTrigger>
              <TabsTrigger value="calendars" className="flex-1" data-testid="tab-calendars">
                <Calendar className="w-3.5 h-3.5 mr-1.5" />
                Calendar
              </TabsTrigger>
            </TabsList>

            <TabsContent value="forms">
          <div className="space-y-4">
            <div className="flex justify-end">
              <Button onClick={handleCreateNew} data-testid="button-create-booking-form">
                <Plus className="w-4 h-4 mr-2" />
                Create New
              </Button>
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : bookingForms.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No booking forms yet. Click "Create New" to add one.
              </div>
            ) : (
              <div className="space-y-3">
                {bookingForms.map((form) => (
                  <Card key={form.id} className={!form.isActive ? "opacity-60" : ""}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-medium">{form.name}</h4>
                            {!form.isActive && <Badge variant="secondary">Inactive</Badge>}
                            {form.isDefault && <Badge variant="default">Default</Badge>}
                          </div>
                          <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                            <LinkIcon className="w-3 h-3" />
                            <code className="text-xs bg-muted px-1 rounded">{getBookingUrl(form)}</code>
                            <a 
                              href={getBookingUrl(form)} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="hover:text-primary"
                            >
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </div>
                          {form.clientTypes && form.clientTypes.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {form.clientTypes.map((type) => (
                                <Badge key={type} variant="outline" className="text-xs">
                                  {type}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleEditForm(form)}
                            data-testid={`button-edit-form-${form.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleDelete(form)}
                            data-testid={`button-delete-form-${form.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
            </TabsContent>

            {/* ── Calendars tab ── */}
            <TabsContent value="calendars">
              <div className="space-y-4">
                {/* Connection status banner */}
                {calendarsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin" />
                  </div>
                ) : calendarsError ? (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>Could not load calendar status.</AlertDescription>
                  </Alert>
                ) : hasTalentCalendar ? (
                  <Alert className="border-green-200 dark:border-green-800">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <AlertDescription>
                      <strong>{talent?.name}'s Google Calendar is connected</strong>
                      {connectedEmail && <span className="ml-1 text-muted-foreground">({connectedEmail})</span>}.
                      Booking availability checks use this calendar directly.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <Alert>
                    <Info className="h-4 w-4" />
                    <AlertDescription>
                      No Google Calendar is connected for {talent?.name}. Bookings will not create calendar events for this talent, and all time slots will appear available.
                    </AlertDescription>
                  </Alert>
                )}

                {/* Connect options — always visible so admin can reconnect */}
                {!calendarsLoading && !calendarsError && (
                  <div className="space-y-3">
                    {/* Option A: Admin-initiated OAuth */}
                    <div className="border rounded-lg p-4 space-y-2">
                      <div className="flex items-center gap-2 font-medium text-sm">
                        <Calendar className="w-4 h-4 text-blue-500" />
                        {hasTalentCalendar ? "Reconnect Calendar" : "Connect via OAuth"}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {hasTalentCalendar
                          ? "Click to replace the connected Google account with a different one."
                          : `Connect a Google Calendar directly — you'll be redirected to Google's consent screen. This links a calendar specifically to ${talent?.name}.`}
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={() => {
                          if (talent) window.location.href = `/api/talent/${talent.id}/calendar/auth`;
                        }}
                        data-testid="button-admin-oauth-calendar"
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        Connect via Google OAuth
                      </Button>
                    </div>

                    {/* Option B: Send invite email to talent */}
                    <div className="border rounded-lg p-4 space-y-2">
                      <div className="flex items-center gap-2 font-medium text-sm">
                        <Mail className="w-4 h-4 text-green-500" />
                        Invite Talent to Self-Connect
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {talent?.email
                          ? `Send ${talent.name} (${talent.email}) a secure one-time link to connect their own Google Calendar.`
                          : "Add an email address to this talent profile to send an invite."}
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        disabled={!talent?.email || sendCalInviteMutation.isPending}
                        onClick={() => sendCalInviteMutation.mutate()}
                        data-testid="button-send-cal-invite-forms-dialog"
                      >
                        {sendCalInviteMutation.isPending ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>
                        ) : (
                          <><Mail className="w-4 h-4 mr-2" />Send Invite Email</>
                        )}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Calendar ID selection (only when connected + calendars available) */}
                {hasTalentCalendar && availableCalendars.length > 0 && (
                  <div className="space-y-3 border-t pt-4">
                    <p className="text-sm font-medium">Which calendars to check for conflicts?</p>
                    <div className="space-y-2 max-h-[240px] overflow-y-auto">
                      {availableCalendars.map((cal) => {
                        const checked = selectedCalendarIds.includes(cal.id);
                        return (
                          <div
                            key={cal.id}
                            className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50"
                            onClick={() =>
                              setSelectedCalendarIds((prev) =>
                                prev.includes(cal.id) ? prev.filter((id) => id !== cal.id) : [...prev, cal.id]
                              )
                            }
                            data-testid={`cal-option-forms-${cal.id}`}
                          >
                            <Checkbox
                              checked={checked}
                              onCheckedChange={() =>
                                setSelectedCalendarIds((prev) =>
                                  prev.includes(cal.id) ? prev.filter((id) => id !== cal.id) : [...prev, cal.id]
                                )
                              }
                            />
                            <div className="flex-1">
                              <Label className="text-sm font-medium cursor-pointer">
                                {cal.summary}
                                {cal.primary && <span className="ml-1 text-xs text-muted-foreground">(Primary)</span>}
                              </Label>
                            </div>
                            {checked && <CheckCircle className="w-4 h-4 text-green-500" />}
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex justify-end">
                      <Button
                        size="sm"
                        onClick={() => updateCalendarsMutation.mutate(selectedCalendarIds)}
                        disabled={updateCalendarsMutation.isPending}
                        data-testid="button-save-calendars-forms-dialog"
                      >
                        {updateCalendarsMutation.isPending ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
                        ) : (
                          <><CheckCircle className="w-4 h-4 mr-2" />Save Selection</>
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        ) : (
          <div className="space-y-6">
            <div className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="formName">Form Name *</Label>
                <Input
                  id="formName"
                  value={formData.name}
                  onChange={(e) => {
                    const name = e.target.value;
                    setFormData(prev => ({
                      ...prev,
                      name,
                      slug: view === "create" ? generateSlug(name) : prev.slug,
                    }));
                  }}
                  placeholder="e.g., Senior Living Booking Form"
                  data-testid="input-form-name"
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="formSlug">URL Slug *</Label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">/book/{talent?.bookingSlug || talent?.id}/</span>
                  <Input
                    id="formSlug"
                    value={formData.slug}
                    onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))}
                    placeholder="senior-living"
                    className="flex-1"
                    data-testid="input-form-slug"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <Label>Client Types (select which types this form applies to)</Label>
              <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto border rounded-md p-3">
                {activeClientTypes.length === 0 ? (
                  <p className="text-sm text-muted-foreground col-span-2">No client types configured</p>
                ) : (
                  activeClientTypes.map((type) => (
                    <div key={type.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`type-${type.id}`}
                        checked={formData.clientTypes.includes(type.name)}
                        onCheckedChange={() => toggleClientType(type.name)}
                        data-testid={`checkbox-client-type-${type.id}`}
                      />
                      <Label htmlFor={`type-${type.id}`} className="text-sm cursor-pointer">
                        {type.name}
                      </Label>
                    </div>
                  ))
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Leave empty to show all client types, or select specific types to limit this form.
              </p>
            </div>

            <div className="space-y-3">
              <Label>Regions (optional: limit to specific regions)</Label>
              <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto border rounded-md p-3">
                {regions.length === 0 ? (
                  <p className="text-sm text-muted-foreground col-span-2">No regions configured</p>
                ) : (
                  regions.map((region) => (
                    <div key={region.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`region-${region.id}`}
                        checked={formData.regionIds.includes(region.id)}
                        onCheckedChange={() => toggleRegion(region.id)}
                        data-testid={`checkbox-region-${region.id}`}
                      />
                      <Label htmlFor={`region-${region.id}`} className="text-sm cursor-pointer">
                        {region.name}
                      </Label>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="space-y-3">
              <Label>Calendars (override talent's default calendars)</Label>
              <div className="grid gap-2 max-h-40 overflow-y-auto border rounded-md p-3">
                {availableCalendars.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No calendars connected</p>
                ) : (
                  availableCalendars.map((calendar) => (
                    <div key={calendar.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`cal-${calendar.id}`}
                        checked={formData.calendarIds.includes(calendar.id)}
                        onCheckedChange={() => toggleCalendar(calendar.id)}
                        data-testid={`checkbox-calendar-${calendar.id}`}
                      />
                      <Label htmlFor={`cal-${calendar.id}`} className="text-sm cursor-pointer">
                        {calendar.summary}
                        {calendar.primary && <Badge variant="secondary" className="ml-2 text-xs">Primary</Badge>}
                      </Label>
                    </div>
                  ))
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Leave empty to use the talent's default calendar settings.
              </p>
              
              <div className="flex items-start gap-2 mt-3 p-3 border rounded-md bg-muted/30">
                <Checkbox
                  id="openAvailability"
                  checked={formData.openAvailability}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, openAvailability: !!checked }))}
                  data-testid="checkbox-open-availability"
                />
                <div className="grid gap-1">
                  <Label htmlFor="openAvailability" className="cursor-pointer font-medium">
                    Open availability - display dates and times confirmed upon booking
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Skip calendar availability checks. The "Select Date" section will change to "Choose Preferred Date" with a note that dates/times will be confirmed.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <Label>Availability Mode</Label>
              <RadioGroup
                value={formData.availabilityMode}
                onValueChange={(value: "calendar" | "custom") => setFormData(prev => ({ ...prev, availabilityMode: value }))}
                className="grid gap-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="calendar" id="mode-calendar" data-testid="radio-availability-calendar" />
                  <Label htmlFor="mode-calendar" className="cursor-pointer">
                    Use Calendar Availability
                  </Label>
                </div>
                <p className="text-xs text-muted-foreground ml-6">
                  Show time slots based on the talent's connected Google Calendar availability.
                </p>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="custom" id="mode-custom" data-testid="radio-availability-custom" />
                  <Label htmlFor="mode-custom" className="cursor-pointer">
                    Custom Time Slots
                  </Label>
                </div>
                <p className="text-xs text-muted-foreground ml-6">
                  Define specific days and time ranges available for booking on this form.
                </p>
              </RadioGroup>
            </div>

            {formData.availabilityMode === "custom" && (
              <div className="space-y-3 border rounded-md p-4 bg-muted/30">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Custom Time Slots
                  </Label>
                  <Button type="button" variant="outline" size="sm" onClick={addTimeslot} data-testid="button-add-timeslot">
                    <Plus className="w-3 h-3 mr-1" />
                    Add Slot
                  </Button>
                </div>
                
                {formData.customTimeslots.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No custom time slots defined. Click "Add Slot" to create one.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {formData.customTimeslots.map((slot, index) => (
                      <div key={index} className="grid grid-cols-12 gap-2 items-end">
                        <div className="col-span-3">
                          <Label className="text-xs">Day</Label>
                          <Select
                            value={slot.day}
                            onValueChange={(value) => updateTimeslot(index, "day", value)}
                          >
                            <SelectTrigger data-testid={`select-timeslot-day-${index}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {DAYS_OF_WEEK.map((day) => (
                                <SelectItem key={day.value} value={day.value}>
                                  {day.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-2">
                          <Label className="text-xs">Start</Label>
                          <Input
                            type="time"
                            value={slot.startTime}
                            onChange={(e) => updateTimeslot(index, "startTime", e.target.value)}
                            data-testid={`input-timeslot-start-${index}`}
                          />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-xs">End</Label>
                          <Input
                            type="time"
                            value={slot.endTime}
                            onChange={(e) => updateTimeslot(index, "endTime", e.target.value)}
                            data-testid={`input-timeslot-end-${index}`}
                          />
                        </div>
                        <div className="col-span-4">
                          <Label className="text-xs">Label (optional)</Label>
                          <Input
                            value={slot.label || ""}
                            onChange={(e) => updateTimeslot(index, "label", e.target.value)}
                            placeholder="e.g., Morning, Afternoon"
                            data-testid={`input-timeslot-label-${index}`}
                          />
                        </div>
                        <div className="col-span-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeTimeslot(index)}
                            data-testid={`button-remove-timeslot-${index}`}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  Define which days and times are available for booking. Visitors will only see these time slots on the form.
                </p>
              </div>
            )}

            <div className="border-t pt-4 space-y-4">
              <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Form Customization (Optional)</h4>
              
              <div className="grid gap-2">
                <Label htmlFor="formTitle">Title Override</Label>
                <Input
                  id="formTitle"
                  value={formData.formTitle}
                  onChange={(e) => setFormData(prev => ({ ...prev, formTitle: e.target.value }))}
                  placeholder={talent?.bookingFormTitle || `Book ${talent?.name}!`}
                  data-testid="input-form-title"
                />
                <p className="text-xs text-muted-foreground">Leave empty to use talent's default title</p>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="formSubtitle">Event Details Override</Label>
                <Textarea
                  id="formSubtitle"
                  value={formData.formSubtitle}
                  onChange={(e) => setFormData(prev => ({ ...prev, formSubtitle: e.target.value }))}
                  placeholder={talent?.bookingFormSubtitle || "Description of the event..."}
                  rows={2}
                  data-testid="input-form-subtitle"
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="formDisclaimer">Disclaimer Override</Label>
                <Input
                  id="formDisclaimer"
                  value={formData.formDisclaimer}
                  onChange={(e) => setFormData(prev => ({ ...prev, formDisclaimer: e.target.value }))}
                  placeholder={talent?.bookingFormDisclaimer || "Optional disclaimer text..."}
                  data-testid="input-form-disclaimer"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="formActive"
                checked={formData.isActive}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, isActive: !!checked }))}
                data-testid="checkbox-form-active"
              />
              <Label htmlFor="formActive">Form is active and accessible</Label>
            </div>

            <div className="flex flex-col items-end gap-2 pt-4 border-t">
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setView("list")} data-testid="button-cancel-form">
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={createMutation.isPending || updateMutation.isPending}
                  data-testid="button-save-form"
                >
                  {(createMutation.isPending || updateMutation.isPending) ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    view === "create" ? "Create Form" : "Save Changes"
                  )}
                </Button>
              </div>
              <button
                type="button"
                onClick={handleOpenAdvanced}
                disabled={createMutation.isPending || updateMutation.isPending}
                className="text-xs text-muted-foreground hover:text-foreground hover:underline disabled:opacity-50"
                data-testid="link-advanced-form-builder"
              >
                {view === "create" ? "Advanced (save and open visual builder)" : "Advanced"}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
